import Bugsnag from "@bugsnag/js";
import dayjs from "dayjs";
import { decodeDelimitedArray, encodeDelimitedArray } from "use-query-params";

import {
  BUTTERSCOTCH,
  DUSTY_ORANGE,
  OLD_MAUVE,
  PICTON_BLUE,
  PLUM_PURPLE,
} from "../components/common/colors";
import {
  amahaProviderReasons,
  childrenFirstProviderReasons,
} from "../metadata/availability";
import { store } from "../Store/configureStore";
import {
  allowedFileTypes,
  CF_PROVIDER_ROLE,
  TYPE_THERAPIST,
} from "../variable";

export const limitString = (string = "", limit = 40) => {
  return string.substring(0, limit) + "...";
};

export const convert12to24 = time12Hour => {
  const [time, period] = time12Hour.split(" ");
  const [hours, minutes] = time.split(":");

  let militaryHours = parseInt(hours);

  if (period === "PM" && militaryHours !== 12) {
    militaryHours += 12;
  } else if (period === "AM" && militaryHours === 12) {
    militaryHours = 0;
  }

  // Format the militaryHours and minutes to have leading zeros if needed
  const militaryHoursStr = militaryHours.toString().padStart(2, "0");
  const minutesStr = minutes.padStart(2, "0");

  return `${militaryHoursStr}:${minutesStr}`;
};

export const inRange = (score, min, max) => {
  return score >= min && score <= max;
};

export const calculateAge = dob => {
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();

  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

export const formatAMPM = date => {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  let ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? "0" + minutes : minutes;
  let strTime = hours + ":" + minutes + " " + ampm;
  return strTime;
};

export const getFullName = (firstname, lastname) =>
  firstname + (lastname ? ` ${lastname}` : "");

export const capitalizeFirstLetter = str => {
  if (!str) return "";
  try {
    return str?.charAt(0)?.toUpperCase() + str?.slice(1, str?.length);
  } catch (error) {
    Bugsnag.notify(error);
  }
};

export const capitalizeWords = str => {
  if (typeof str !== "string") return "";

  return str
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const splitAndJoinString = (
  str = "",
  separator = " ",
  concatenator = ", "
) => {
  try {
    return str.split(separator).join(concatenator);
  } catch (err) {
    Bugsnag.notify(err);
  }
};

export const getFileNameWithTimestamp = fname => {
  let extension = fname.split(".").pop();
  let filename = fname
    .trim()
    .replace(/\.[^/.]+$/, "")
    .replace(" ", "_");
  return `${filename}${new Date().getTime()}.${extension}`;
};

export const HumanizeDay = (dateTime, format = "D MM") => {
  const isYesterday = new Date().getDate() - new Date(dateTime).getDate() === 1;

  const dayOfNextAvailableDate = dayjs(dateTime).isSame(dayjs(), "day")
    ? "Today"
    : isYesterday
      ? "Yesterday"
      : dayjs(dateTime).format(format);

  return dayOfNextAvailableDate;
};

export const getTextContentFromHtml = html => {
  if (typeof html !== "string") return "";

  // Replace HTML entities like &nbsp; with their corresponding characters
  const decodedHtml = html.replace(/&nbsp;/g, " ");

  // Replace HTML tags with an empty string
  const textWithoutTags = decodedHtml.replace(/<[^>]+>/g, "");

  // Replace multiple whitespaces (including newlines and tabs) with a single space
  // Also trim leading and trailing whitespaces
  return textWithoutTags.replace(/\s+/g, " ").trim();
};

export const isValidNumberInput = (value, length) => {
  if (
    value.search(/\s/) !== -1 ||
    value.length > length ||
    Number.isNaN(+value)
  )
    return false;
  return true;
};

export const numbersOnly = e => {
  if (
    (e.keyCode >= 48 && e.keyCode <= 57) ||
    (e.keyCode >= 96 && e.keyCode <= 105) ||
    e.keyCode === 8
  ) {
    return false;
  } else {
    e.preventDefault();
  }
};

export const currentFlow = (providerProfile, clientProfile) => {
  const providerId = providerProfile?.id;
  const { mytherapist, my_couple_therapist } = clientProfile;
  try {
    const providerType = providerProfile?.usertype;

    const isOnlyCoupleTherapist =
      my_couple_therapist?.includes(providerId) &&
      !mytherapist?.includes(providerId);

    if (providerType === TYPE_THERAPIST) {
      if (isOnlyCoupleTherapist) return "couples";

      return "therapy";
    }
    return "psychiatry";
  } catch (err) {
    Bugsnag.notify(err);
  }
};

// For supervision role name
// MDT-Lead-Moderator -> mdt_lead_moderator
export const getAnalyticsRoleName = (roleName = "") => {
  const strings = roleName.split("-");
  return strings.map(string => string.toLowerCase()).join("_");
};

// To extract latest Amaha designation from provider's affiliations
export const checkDesignation = provider => {
  let designation;
  let latestAffiliation;

  if (!provider?.affiliations?.length) designation = null;

  if (provider?.affiliations?.length === 1) {
    latestAffiliation = provider?.affiliations?.[0];
    if (
      latestAffiliation?.organisation?.toLowerCase().includes("amaha") ||
      latestAffiliation?.organisation?.toLowerCase().includes("innerhour")
    ) {
      designation = latestAffiliation?.designation;
    }
  } else {
    provider?.affiliations?.forEach(affiliation => {
      if (
        affiliation?.organisation?.toLowerCase().includes("amaha") ||
        affiliation?.organisation?.toLowerCase().includes("innerhour")
      ) {
        designation = affiliation?.designation;
      }
    });
  }

  return designation;
};

// return 24 format time to AM/PM
export const formatSlotTime = slot => {
  const splitTime = slot?.split(":");
  const hours = splitTime?.[0];
  const mins = splitTime?.[1];

  return hours >= 12
    ? `${hours % 12 === 0 ? 12 : hours % 12}:${mins} PM`
    : `${slot} AM`;
};

export const generateRange = value => {
  // Calculate the second number in the range
  let secondNumber = Math.ceil(value / 5) * 5;
  // Create the array of two whole numbers
  let rangeArray;
  if (value < 1) {
    rangeArray = [0, 4];
  } else {
    rangeArray = [1, secondNumber];
  }
  // Return the array
  return rangeArray;
};

export const getRandomColors = dataArray => {
  const colorArray = [];
  dataArray?.forEach(() => {
    const randomColor = `hsla(${Math.random() * 360}, 100%, 40%, 1)`;
    if (!colorArray.includes(randomColor)) {
      colorArray.push(randomColor);
    }
  });
  return colorArray;
};

export const getPredefinedColors = number => {
  const colors = [
    PICTON_BLUE[500],
    DUSTY_ORANGE[500],
    OLD_MAUVE[500],
    BUTTERSCOTCH[500],
    PLUM_PURPLE[500],
  ];
  const hoverColors = [
    PICTON_BLUE[600],
    DUSTY_ORANGE[600],
    OLD_MAUVE[600],
    BUTTERSCOTCH[600],
    PLUM_PURPLE[600],
  ];

  const arrayOfColors = [];
  for (let i = 0; i < number; i++) {
    if (i >= colors.length) {
      const colorIndex = i % colors.length;
      arrayOfColors.push({
        color: colors[colorIndex],
        hoverColor: hoverColors[colorIndex],
      });
    } else {
      arrayOfColors.push({ color: colors[i], hoverColor: hoverColors[i] });
    }
  }

  return arrayOfColors;
};

// this one converts 00:00 to 12:00 AM
export const formatSlot = slot => {
  if (!/^\d{2}:\d{2}$/.test(slot)) {
    return "Invalid time format";
  }

  const splitTime = slot.split(":");
  const hours = splitTime[0];
  const mins = splitTime[1];

  if (hours === "00") {
    return `12:${mins} AM`;
  }

  return hours >= 12
    ? `${hours % 12 === 0 ? 12 : hours % 12}:${mins} PM`
    : `${hours}:${mins} AM`;
};

/**
 * Checks if `value` is an empty object, collection, map, or set.
 *
 * Objects are considered empty if they have no own enumerable string keyed
 * properties.
 *
 * Array-like values such as `arguments` objects, arrays, buffers, strings, or
 * jQuery-like collections are considered empty if they have a `length` of `0`.
 * Similarly, maps and sets are considered empty if they have a `size` of `0`.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is empty, else `false`.
 * @example
 *
 * isEmpty(null)
 * // => true
 *
 * isEmpty(true)
 * // => true
 *
 * isEmpty(1)
 * // => true
 *
 * isEmpty([1, 2, 3])
 * // => false
 *
 * isEmpty('abc')
 * // => false
 *
 * isEmpty({ 'a': 1 })
 * // => false
 */

export const isEmpty = value => {
  const toString = Object.prototype.toString;

  if (value == null) {
    return true;
  }

  if (
    value != null &&
    (Array.isArray(value) ||
      typeof value === "string" ||
      typeof value.splice === "function")
  ) {
    return !value.length;
  }

  const tag = toString.call(value);

  if (tag === "[object Map]" || tag === "[object Set]") {
    return !value.size;
  }

  if (typeof value === "object") {
    for (const key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        return false;
      }
    }
  }

  return true;
};

export const getSlotsForAnalytics = slots => {
  const finalSlots = {};

  try {
    if (!slots) return finalSlots;
    const user = store?.getState()?.auth?.user;
    const isCfProvider = user?.roles?.some(role =>
      CF_PROVIDER_ROLE.includes(role)
    );

    finalSlots["booked_slots"] = slots.booked_slots?.map(({ slot }) => slot);
    finalSlots["online"] = [];
    slots.online_slots.forEach(({ slots }) => {
      finalSlots["online"] = [...finalSlots["online"], ...slots];
    });
    finalSlots["offline"] = [];
    slots.offline_slots.forEach(({ slots }) => {
      finalSlots["offline"] = [...finalSlots["offline"], ...slots];
    });
    finalSlots["both"] = [];
    slots.both_slots.forEach(({ slots }) => {
      finalSlots["both"] = [...finalSlots["both"], ...slots];
    });
    let acceptedReasons;
    if (isCfProvider) {
      acceptedReasons = childrenFirstProviderReasons;
    } else {
      acceptedReasons = amahaProviderReasons;
    }
    slots.blocked_slots.forEach(blockedSlot => {
      const reason = blockedSlot.short_reason;

      const finalReason = acceptedReasons.some(({ label }) => label === reason)
        ? reason
        : "Other";

      if (finalSlots[finalReason]) {
        finalSlots[finalReason].push(blockedSlot.slot);
      } else {
        finalSlots[finalReason] = [blockedSlot.slot];
      }
    });
  } catch (err) {
    Bugsnag.notify(err);
  }

  return finalSlots;
};

const FREQUENCY_REPEAT_SLUG = {
  0: "none",
  1: "daily",
  2: "weekly",
  3: "fortnightly",
  4: "monthly",
};

const getRepeatFrequency = value => {
  if (value === "does_not_repeat") {
    return "none";
  } else if (value === "daily") {
    return "daily";
  } else if (typeof value === "string" && value.startsWith("weekly")) {
    return "weekly";
  } else if (typeof value === "string" && value.startsWith("alternate")) {
    return "fortnightly";
  } else if (typeof value === "string" && value.startsWith("monthly")) {
    return "monthly";
  } else {
    return "unknown";
  }
};
export const getBlockedSlotsForAnalytics = (
  intervals,
  combinedCalendarValues,
  combinedRepeatValues,
  repeatOptions
) => {
  const payload = [];
  try {
    intervals.forEach((interval, index) => {
      const indexOfInterval = repeatOptions?.findIndex(
        item => item?.value === combinedRepeatValues[index]?.value
      );
      const frequency = FREQUENCY_REPEAT_SLUG[indexOfInterval] || "none";
      const obj = {
        reason: interval?.short_reason,
        blocked_from: interval?.start_date,
        blocked_till:
          indexOfInterval === 0
            ? interval?.start_date
            : dayjs(combinedCalendarValues[index]).format("YYYY-MM-DD"),
        start_time: interval?.start_slot,
        end_time: interval?.end_slot,
        frequency,
      };
      payload.push(obj);
    });
    return payload;
  } catch (error) {
    Bugsnag.notify(error);
    return {};
  }
};
export const getUndoSlots = (slotData, restData, inUndoAll) => {
  try {
    let isSlotUnblockedInBetween = false;

    if (inUndoAll) {
      const findUnblocked = restData?.find(
        item => item?.status === "unblocked"
      );
      if (findUnblocked) {
        isSlotUnblockedInBetween = true;
      }
    }

    if (isSlotUnblockedInBetween) {
      const filteredBlocked = restData?.filter(
        item => item?.status === "blocked"
      );

      // Sort blocked slots by date
      const sortedBlocked = [...filteredBlocked].sort(
        (a, b) => new Date(a.start_date) - new Date(b.start_date)
      );

      const grouped = [];
      let currentGroup = null;

      for (let i = 0; i < sortedBlocked.length; i++) {
        const current = sortedBlocked[i];
        const next = sortedBlocked[i + 1];
        const currDate = new Date(current.start_date);
        if (!currentGroup) {
          currentGroup = {
            reason: current.short_reason,
            blocked_from: current.start_date,
            blocked_till: current.start_date,
            start_time: current.start_slot,
            end_time: current.end_slot,
            frequency: getRepeatFrequency(slotData?.repeat_on),
          };
        }

        const expectedNext = new Date(currDate);
        expectedNext.setDate(expectedNext.getDate() + 1);

        const isNextConsecutive =
          next &&
          next.status === "blocked" &&
          new Date(next.start_date).getTime() === expectedNext.getTime();

        if (!isNextConsecutive) {
          currentGroup.blocked_till = current.start_date;
          grouped.push(currentGroup);
          currentGroup = null;
        }
      }

      return grouped;
    } else {
      return {
        reason: slotData?.short_reason,
        blocked_from: slotData?.start_date,
        blocked_till: slotData?.start_date,
        start_time: slotData?.start_slot,
        end_time: slotData?.end_slot,
        frequency: getRepeatFrequency(
          slotData?.repeat_on || restData?.[0]?.repeat_on
        ),
      };
    }
  } catch (error) {
    console.log(error);
    Bugsnag.notify(error);
    return {};
  }
};

export const validateFile = (file, fileSizeInMb = 100) => {
  const fileSizeLimit = fileSizeInMb * 1000 * 1000; // decimal MB (matches OS display)

  // Check file type
  if (!allowedFileTypes.includes(file.type)) {
    alert("Please upload valid file format.");
    return {
      isValid: false,
      message: "Please upload a valid file format.",
    };
  }

  // Human-readable size (in MB or KB - decimal)
  let size = null;
  if (file.size >= 1000 * 1000) {
    size = `${(file.size / 1000 / 1000).toFixed(2)} MB`;
  } else {
    size = `${(file.size / 1000).toFixed(2)} KB`;
  }

  // Check size limit
  if (file.size > fileSizeLimit) {
    alert(`Maximum allowed file size is ${fileSizeInMb} MB.`);
    return {
      isValid: false,
      fileSize: size,
      message: `Maximum allowed file size is ${fileSizeInMb} MB.`,
    };
  }

  return {
    isValid: true,
    fileSize: size,
  };
};

/** Uses a comma to delimit entries. e.g. ['a', 'b'] => qp?=a,b */
export const CommaArrayParam = {
  encode: array => encodeDelimitedArray(array, ","),
  decode: array => decodeDelimitedArray(array, ","),
};

export const getProviderType = providerType => {
  return providerType === "care_coordinator"
    ? "care-coordinator"
    : providerType;
};

export const validIdGenerator = value => {
  return (value || "").toLowerCase().replace(/[\s/_]+/g, "-");
};
